/**
 * §15 G7: the command palette — Ctrl/⌘ K anywhere opens one box that reaches every page this person may open, every
 * event and property, and every setting by name. Arrow keys move, Enter goes, Esc closes. It lists only what the
 * route table and capabilities allow; nothing here is a second navigation to keep in sync.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useMatch, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Search } from 'lucide-react';
import { ROUTES, href } from '@/routes/table';
import { useSession } from '@/lib/session';
import { supabase } from '@/lib/supabase';
import { SETTINGS } from '@/features/settings';
import { Dialog } from '@/components/ui/Dialog';
import { cn } from '@/lib/cn';
import { dateRange } from '@/lib/format';
import { track } from '@/lib/telemetry';

/** The header button (pointer and touch) opens the same palette as Ctrl/⌘ K. */
export const OPEN_PALETTE = 'rmp2:open-palette';
export function PaletteButton() {
  const { session } = useSession();
  if (session.state !== 'authenticated-single-tenant') return null;
  return (
    <button className="inline-flex size-9 items-center justify-center rounded-control text-ink-2 hover:bg-surface-2 hover:text-ink" aria-label="Go to anything" title="Go to anything (Ctrl/⌘ K)"
      onClick={() => window.dispatchEvent(new Event(OPEN_PALETTE))} data-testid="palette-button">
      <Search className="size-4" aria-hidden />
    </button>
  );
}

interface Item { id: string; group: 'Pages' | 'This event' | 'Events' | 'Properties' | 'Settings'; label: string; hint?: string; to: string }

/** Every word typed must appear somewhere in the label or the hint (order-free, case-free). */
export function paletteMatch(item: Pick<Item, 'label' | 'hint'>, q: string): boolean {
  const hay = `${item.label} ${item.hint ?? ''}`.toLowerCase();
  return q.toLowerCase().split(/\s+/).filter(Boolean).every((w) => hay.includes(w));
}

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [active, setActive] = useState(0);
  const { session, can } = useSession();
  const navigate = useNavigate();
  const eventMatch = useMatch('/events/:eventId/*');
  const eventId = eventMatch?.params.eventId;
  const listRef = useRef<HTMLUListElement>(null);
  const member = session.state === 'authenticated-single-tenant';

  useEffect(() => {
    const show = () => { setQ(''); setActive(0); setOpen(true); track('palette-opened'); };
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && !e.altKey && e.key.toLowerCase() === 'k') {
        if (!member || document.querySelector('[data-testid="floor-plan-studio"]')) return;
        e.preventDefault();
        show();
      }
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener(OPEN_PALETTE, show);
    return () => { window.removeEventListener('keydown', onKey); window.removeEventListener(OPEN_PALETTE, show); };
  }, [member]);

  const events = useQuery({
    queryKey: ['palette-events'],
    enabled: open && member,
    queryFn: async () => ((await supabase.rpc('event_summaries')).data ?? []) as Array<{ event_id: string; event_name: string; start_date: string; end_date: string; deleted_at: string | null; hotels: string[] }>,
  });
  const hotels = useQuery({
    queryKey: ['palette-hotels'],
    enabled: open && member && can('property.read'),
    queryFn: async () => ((await supabase.rpc('hotel_summaries')).data ?? []) as Array<{ hotel_id: string; hotel_name: string; city: string | null }>,
  });

  const items = useMemo<Item[]>(() => {
    const out: Item[] = [];
    for (const r of ROUTES) {
      if (r.capability && !can(r.capability)) continue;
      // the rail's pages, plus the person's own pages (account, what's new), which live in the header, not the rail
      if (r.area === 'account' && !r.path.includes(':')) out.push({ id: `p:${r.id}`, group: 'Pages', label: r.nav?.label ?? r.title, to: r.path });
      if (!r.nav) continue;
      if (r.area === 'org') out.push({ id: `p:${r.id}`, group: 'Pages', label: r.nav.label, to: r.path });
      if (r.area === 'event' && eventId) out.push({ id: `e:${r.id}`, group: 'This event', label: r.nav.label, to: href(r.id, { eventId }) });
    }
    for (const e of events.data ?? []) if (!e.deleted_at) out.push({ id: `ev:${e.event_id}`, group: 'Events', label: e.event_name, hint: `${dateRange(e.start_date, e.end_date)} ${e.hotels.join(' ')}`, to: `/events/${e.event_id}` });
    for (const h of hotels.data ?? []) out.push({ id: `h:${h.hotel_id}`, group: 'Properties', label: h.hotel_name, hint: h.city ?? undefined, to: `/properties/${h.hotel_id}` });
    for (const s of SETTINGS) {
      if (s.scope === 'organization' && !can('settings.manage')) continue;
      if (s.scope === 'event' && (!eventId || !can('event.manage'))) continue;
      const base = s.scope === 'organization' ? '/organization' : s.scope === 'account' ? '/account' : `/events/${eventId}/settings`;
      out.push({ id: `s:${s.scope}:${s.anchor}`, group: 'Settings', label: s.title, hint: `${s.description} ${s.synonyms.join(' ')}`, to: `${base}#${s.anchor}` });
    }
    return out;
  }, [can, eventId, events.data, hotels.data]);

  const shown = useMemo(() => (q.trim() ? items.filter((i) => paletteMatch(i, q)) : items.filter((i) => i.group !== 'Settings')).slice(0, 60), [items, q]);
  useEffect(() => setActive(0), [q]);
  useEffect(() => { listRef.current?.querySelector(`[data-index="${active}"]`)?.scrollIntoView({ block: 'nearest' }); }, [active]);

  const go = (i: Item | undefined) => {
    if (!i) return;
    setOpen(false);
    navigate(i.to);
  };
  let lastGroup = '';
  return (
    <Dialog open={open} onClose={() => setOpen(false)} title="Go to anything" description="Pages, events, properties and settings — type a few letters, then Enter." size="lg">
      <div className="flex flex-col gap-3" data-testid="command-palette">
        <label className="relative block">
          <span className="sr-only">Search pages, events, properties and settings</span>
          <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted" aria-hidden />
          <input autoFocus role="combobox" aria-expanded="true" aria-controls="palette-list" aria-activedescendant={shown[active] ? `palette-${active}` : undefined}
            value={q} onChange={(e) => setQ(e.target.value)} placeholder="e.g. rooms, retreat, colours"
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(shown.length - 1, a + 1)); }
              else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(0, a - 1)); }
              else if (e.key === 'Enter') { e.preventDefault(); go(shown[active]); }
            }}
            className="h-control w-full rounded-control bg-surface pl-8 pr-3 text-sm text-ink ring-1 ring-input focus-visible:outline-2 focus-visible:outline-focus" />
        </label>
        <ul id="palette-list" role="listbox" aria-label="Results" ref={listRef} className="max-h-[50dvh] overflow-y-auto">
          {!shown.length && <li className="px-2 py-4 text-sm text-muted">Nothing matches “{q.trim()}”.</li>}
          {shown.map((it, i) => {
            const heading = it.group !== lastGroup ? it.group : null;
            lastGroup = it.group;
            return (
              <li key={it.id} role="presentation">
                {heading && <div className="px-2 pb-1 pt-3 text-[11px] font-semibold uppercase tracking-wide text-muted" aria-hidden>{heading}</div>}
                <div id={`palette-${i}`} role="option" aria-selected={i === active} data-index={i} onMouseEnter={() => setActive(i)} onClick={() => go(it)}
                  className={cn('flex cursor-pointer items-baseline justify-between gap-3 rounded-control px-2 py-1.5 text-sm', i === active ? 'bg-brand-tint text-ink' : 'text-ink-2')}>
                  <span className="truncate font-medium">{it.label}</span>
                  <span className="shrink-0 text-[12px] text-muted">{it.group === 'Settings' ? 'Setting' : it.group === 'This event' ? 'This event' : it.hint?.split(' ').slice(0, 4).join(' ')}</span>
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </Dialog>
  );
}
